package com.siriusxm.pia.views.unifiedaggregator.relationships

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.success
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.*
import com.siriusxm.pia.views.unifiedaggregator.*
import com.siriusxm.unifiedcontent.console.aggregator.RelationshipOperation
import com.siriusxm.unifiedcontent.console.aggregator.allowsRelationship
import com.siriusxm.unifiedcontent.console.aggregator.consoleProducer
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.relationshipUpdateCapability
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

/**
 * A view for creating or editing relationships
 * @param schema the schema to use. If not provided, all schemas will be available
 * @param entity one side of the relationship. If not provided, both entities will be provided. A provided
 * entity will also limit the relationship schemas that are made available.
 * @param relationship the existing relationship to edit. When saved, this relationship will be removed
 * and a new one added (you can't edit a relationship).
 *
 * Relationships are created as the console's own producer (UCA-1152), so only types the console
 * producer allows are offered. Before submitting, each subject/object pair is checked against AVP
 * (`UnifiedContentAggregator_RelationshipUpdate` with `operation` CREATE, UCA-1151) in one batch. A
 * pair AVP denies is reported and skipped rather than sent. That check only saves a round trip; the
 * aggregator decides each request itself.
 */
@Composable
fun relationshipEditor(
    context: AggregatorService, schema: RelationshipSchema? = null,
    entity: Entity? = null, relationship: EntityRelationship? = null,
    returnPath: String? = null
) {
    val availableSchemas: List<RelationshipSchema> = if (relationship != null) {
        context.relationships.filter { it.id == relationship.schemaId }
    } else if (schema != null) {
        listOf(schema)
    } else if (entity != null) {
        context.relationships.filter {
            it.objectType.name == entity.type || it.subjectType.name == entity.type
        }
    } else {
        context.relationships
    }.filter { it.editable }.let { schemas ->
        val producer = consoleProducer(context.producers, context.context.configuration.clientId)
        schemas.filter { producer?.allowsRelationship(it.id) == true }
    }.sortedBy { it.name.lowercase() }

    /**
     * Ask AVP, in one batch, whether each (subject, object) pair may be CREATEd for [schemaId]. Fails
     * closed: an error leaves every pair denied.
     */
    suspend fun allowedPairs(schemaId: String, pairs: List<Pair<String, String>>): Set<Pair<String, String>> {
        val queries = pairs.associateWith { (subjectId, objectId) ->
            relationshipUpdateCapability(schemaId, RelationshipOperation.CREATE, subjectId, objectId)
        }
        val decisions = try {
            context.context.capabilities.refresh(queries.values)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("AVP capability check failed; treating the relationships as denied", e)
            emptyMap()
        }
        return queries.filterValues { decisions[it] == true }.keys
    }

    var selectedSchema by remember { mutableStateOf(availableSchemas.firstOrNull()) }
    var subject by remember { mutableStateOf(relationship?.subjectEntity) }
    var `object` by remember { mutableStateOf(relationship?.objectEntity) }

    val multiSelectMode = relationship == null
    var selectedEntities by remember { mutableStateOf(listOf<Entity>()) }
    var submitting by remember { mutableStateOf(false) }

    LaunchedEffect(selectedSchema) {
        if (entity != null) {
            if (entity.type == selectedSchema?.subjectType?.name) {
                subject = entity
                `object` = null
            } else if (entity.type == selectedSchema?.objectType?.name) {
                `object` = entity
                subject = null
            }
        }
        if (multiSelectMode) {
            selectedEntities = emptyList()
        }
    }

    dialogView(if (relationship != null) "Edit Relationship" else "Create Relationship") {
        subTitle = selectedSchema?.name
        content {
            // Boxes own no outer margin; the column provides the gap between stacked boxes.
            Column(gap = 20.px) {
                if (availableSchemas.size > 1) {
                    box {
                        dialogField("Relationship") {
                            Select({
                                onChange { update ->
                                    selectedSchema = availableSchemas.find { it.id == update.value }
                                }
                            }) {
                                availableSchemas.sortedBy { it.name.lowercase() }.forEach {
                                    Option(it.id, {
                                        if (it == selectedSchema) selected()
                                    }) {
                                        Text(it.name)
                                    }
                                }
                            }
                        }
                    }
                }

                @Composable
                fun searchResults(searchStatus: SearchStatus, onSelect: (Entity?) -> Unit) {
                    if (!searchStatus.loading && searchStatus.results.isNotEmpty()) {
                        entityList(context, searchStatus.results) {
                            onSelect(it)
                        }
                    }
                }

                @Composable
                fun relationshipEntitySelector(type: EntityType, selection: Entity? = null, cb: (Entity?) -> Unit) {
                    var searchResults by remember { mutableStateOf<SearchStatus?>(null) }
                    if (entity != null && entity.type == type) {
                        entityListItem(context, entity, false)
                    } else {
                        if (selection != null) {
                            entityListItem(context, selection, false) {
                                cb(null)
                            }
                        } else {
                            entitySearch(context, initType = type, showTypes = false) {
                                searchResults = it
                            }

                            searchResults?.let {
                                searchResults(it) {
                                    cb(it)
                                }
                            }
                        }
                    }
                }

                @Composable
                fun multiSelectSide(
                    typeName: String,
                    selectedList: List<Entity>,
                    onAdd: (Entity) -> Unit,
                    onRemove: (Entity) -> Unit
                ) {
                    Column(gap = 1.em) {
                        var sideSearchResults by remember { mutableStateOf<SearchStatus?>(null) }
                        var searchKey by remember { mutableStateOf(0) }
                        key(searchKey) {
                            entitySearch(context, initType = typeName, showTypes = false) {
                                sideSearchResults = it
                            }
                        }
                        sideSearchResults?.let { status ->
                            if (!status.loading && status.results.isNotEmpty()) {
                                val selectedIds = selectedList.map { it.id }.toSet()
                                val filtered = status.results.filter { it.id !in selectedIds }
                                if (filtered.isNotEmpty()) {
                                    Div({ classes(AggregatorStyles.searchResultsScrollable) }) {
                                        entityList(context, filtered, includeLinks = false) { selected ->
                                            onAdd(selected)
                                            sideSearchResults = null
                                            searchKey++
                                        }
                                    }
                                }
                            }
                        }
                        if (selectedList.isNotEmpty()) {
                            dialogField("Selected") {
                                Div({ classes(AggregatorStyles.entityListSelected) }) {
                                    selectedList.forEach { selected ->
                                        entityListItem(context, selected, false, actions = {
                                            icon("close") {
                                                size = IconSize.TINY
                                                type = IconType.SYMBOLS
                                                action {
                                                    onRemove(selected)
                                                }
                                            }
                                        })
                                    }
                                }
                            }
                        }
                    }
                }

                fun entityTypeName(typeId: String): String {
                    return context.entityType(typeId)?.name ?: typeId
                }

                if (selectedSchema != null) {
                    if (multiSelectMode) {
                        val multiSelectOnSubjectSide =
                            entity != null && entity.type == selectedSchema?.objectType?.name

                        // Object side
                        box(entityTypeName(selectedSchema!!.objectType.name)) {
                            if (multiSelectOnSubjectSide) {
                                relationshipEntitySelector(selectedSchema!!.objectType.name, `object`) {
                                    `object` = it
                                }
                            } else {
                                multiSelectSide(
                                    selectedSchema!!.objectType.name,
                                    selectedEntities,
                                    onAdd = { selectedEntities = selectedEntities + it },
                                    onRemove = { e -> selectedEntities = selectedEntities.filter { it.id != e.id } }
                                )
                            }
                        }

                        // Subject side
                        box(entityTypeName(selectedSchema!!.subjectType.name)) {
                            if (multiSelectOnSubjectSide) {
                                multiSelectSide(
                                    selectedSchema!!.subjectType.name,
                                    selectedEntities,
                                    onAdd = { selectedEntities = selectedEntities + it },
                                    onRemove = { e -> selectedEntities = selectedEntities.filter { it.id != e.id } }
                                )
                            } else {
                                relationshipEntitySelector(selectedSchema!!.subjectType.name, subject) {
                                    subject = it
                                }
                            }
                        }
                    } else {
                        // Edit mode — single-select both sides
                        box(entityTypeName(selectedSchema!!.objectType.name)) {
                            relationshipEntitySelector(selectedSchema!!.objectType.name, `object`) {
                                `object` = it
                            }
                        }

                        box(entityTypeName(selectedSchema!!.subjectType.name)) {
                            relationshipEntitySelector(selectedSchema!!.subjectType.name, subject) {
                                subject = it
                            }
                        }
                    }
                }
            }
        }

        fun navReturn() {
            if (returnPath != null) {
                context.navigate(returnPath)
            } else if (entity != null) {
                context.navigate("entity/${encodeURIComponent(entity.id)}/relationships")
            } else {
                // `toString()` rather than `orEmpty()`: a null schema has always produced the literal
                // segment "null" here, and this change is about encoding, not about that behaviour.
                context.navigate("relationships/${encodeURIComponent(selectedSchema?.id.toString())}")
            }
        }

        if (multiSelectMode) {
            val effectiveSchema = selectedSchema
            // Determine whether multi-select entities are on the subject or object side
            val multiSelectOnSubjectSide =
                entity != null && entity.type == effectiveSchema?.objectType?.name
            // The fixed side: either the manually-picked single entity or the pre-selected entity
            val fixedSubject = if (!multiSelectOnSubjectSide) subject else null
            val fixedObject = if (multiSelectOnSubjectSide) `object` else null
            val hasFixedSide = fixedSubject != null || fixedObject != null

            if (effectiveSchema != null && hasFixedSide && selectedEntities.isNotEmpty()) {
                action {
                    val count = selectedEntities.size
                    title = "Submit"
                    primary = true
                    enabled = !submitting
                    showProgressOnAction = true
                    this.action {
                        submitting = true
                        try {
                            fun pairFor(selectedEntity: Entity): Pair<String, String> =
                                (fixedSubject?.id ?: selectedEntity.id) to (fixedObject?.id ?: selectedEntity.id)

                            val allowed = allowedPairs(effectiveSchema.id, selectedEntities.map(::pairFor))
                            val (permitted, denied) = selectedEntities.partition { pairFor(it) in allowed }
                            if (denied.isNotEmpty()) {
                                context.notifications.error(
                                    "You do not have permission to create ${denied.size} of these relationships, " +
                                        "so they were not submitted."
                                )
                            }

                            val created = mutableListOf<String>()
                            for (chunk in permitted.chunked(5)) {
                                val results = coroutineScope {
                                    chunk.map { selectedEntity ->
                                        async {
                                            val (subjectId, objectId) = pairFor(selectedEntity)
                                            try {
                                                context.clientApi.submitRelationship(
                                                    effectiveSchema.id,
                                                    IncomingRelationshipUpdate(subjectId, objectId, null).toSdk()
                                                )
                                                selectedEntity.id
                                            } catch (e: CancellationException) {
                                                throw e
                                            } catch (t: Throwable) {
                                                val error = intakeErrorMessage("create $subjectId → $objectId", t)
                                                context.notifications.error(error.message, error.description)
                                                null
                                            }
                                        }
                                    }.awaitAll().filterNotNull()
                                }
                                created.addAll(results)
                            }
                            // Poll for all relationships to appear (up to 10 seconds)
                            val pending = created.toMutableSet()
                            for (i in 1..10) {
                                if (pending.isEmpty()) break
                                delay(1000)
                                val confirmed = pending.filter { entityId ->
                                    try {
                                        val subId = fixedSubject?.id ?: entityId
                                        val objId = fixedObject?.id ?: entityId
                                        context.clientApi.getRelationship(
                                            effectiveSchema.id, subId, objId
                                        )
                                        true
                                    } catch (_: Throwable) {
                                        false
                                    }
                                }
                                pending.removeAll(confirmed.toSet())
                            }
                            if (created.size == count) {
                                context.notifications.success(
                                    "$count relationship${if (count > 1) "s" else ""} created successfully",
                                    autoDismiss = 5.seconds
                                )
                            } else {
                                context.notifications.error(
                                    "${created.size} of $count created. ${count - created.size} failed."
                                )
                            }
                            navReturn()
                        } finally {
                            submitting = false
                        }
                    }
                }
            }
        } else if (`object` != null && subject != null && schema != null) {
            action {
                title = "Submit"
                primary = true
                showProgressOnAction = true
                this.action {
                    val pair = subject?.id!! to `object`?.id!!
                    if (pair !in allowedPairs(schema.id, listOf(pair))) {
                        context.notifications.error("You do not have permission to create this relationship.")
                        return@action
                    }
                    try {
                        context.clientApi.submitRelationship(
                            schema.id, IncomingRelationshipUpdate(pair.first, pair.second, null).toSdk()
                        )
                        delay(5000)
                        navReturn()
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Throwable) {
                        val error = intakeErrorMessage("create this relationship", e)
                        context.notifications.error(error.message, error.description)
                    }
                }
            }
        }
        action("Cancel") {
            navReturn()
        }
    }
}
